import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../api/client';
import {
  normalizePhoneInput,
  validateEmailCom,
  validatePhone10Digits,
} from '../utils/formValidation';
import PhoneInput from '../components/PhoneInput';
import PageTemplate from './PageTemplate';
import './ModulePage.css';

function EditTeacher() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    course: '',
    password: '',
    confirmPassword: '',
    role: 'teacher',
  });
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      setLoading(true);
      setError('');
      try {
        const [teacherRes, coursesRes] = await Promise.all([
          apiClient.get(`/api/auth/teachers/${id}/`),
          apiClient.get('/api/courses/'),
        ]);
        if (cancelled) return;

        const teacher = teacherRes.data?.teacher || teacherRes.data;
        const coursesList = Array.isArray(coursesRes.data)
          ? coursesRes.data
          : coursesRes.data?.courses?.results || coursesRes.data?.courses || [];
        setCourses(Array.isArray(coursesList) ? coursesList : []);

        const courseName = teacher.course_name || teacher.course?.course_name || '';
        let courseId = '';
        if (courseName) {
          const matched = coursesList.find(
            (c) => (c.course_name || c.name || '').toLowerCase() === courseName.toLowerCase()
          );
          courseId = matched ? String(matched.course_id || matched.id) : '';
        }

        setForm({
          name: teacher.full_name || teacher.name || '',
          email: teacher.email || '',
          phone: teacher.phone_number || teacher.phone || '',
          course: courseId,
          password: '',
          confirmPassword: '',
          role: teacher.role || 'teacher',
        });
      } catch (e) {
        if (!cancelled) {
          if (e?.response?.status === 404) {
            setError('Teacher not found.');
            setTimeout(() => navigate('/teachers'), 2000);
          } else {
            setError(
              e?.response?.data?.detail ||
                e?.response?.data?.message ||
                'Unable to load teacher.'
            );
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingCourses(false);
        }
      }
    }

    if (id) loadData();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === 'phone' ? normalizePhoneInput(value) : value;
    setForm((prev) => ({ ...prev, [name]: nextValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const trimmed = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      password: form.password,
      confirmPassword: form.confirmPassword,
    };

    if (!trimmed.name) {
      setError('Full name is required.');
      return;
    }

    const emailError = validateEmailCom(trimmed.email);
    if (emailError) {
      setError(emailError);
      return;
    }

    const phoneError = validatePhone10Digits(trimmed.phone);
    if (phoneError) {
      setError(phoneError);
      return;
    }

    if (
      (trimmed.password || trimmed.confirmPassword) &&
      trimmed.password !== trimmed.confirmPassword
    ) {
      setError('Password and Confirm Password must match.');
      return;
    }

    if (trimmed.password && trimmed.password.length < 5) {
      setError('Password should be at least 5 characters long.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        full_name: trimmed.name,
        email: trimmed.email,
        phone_number: normalizePhoneInput(trimmed.phone),
        role: form.role || 'teacher',
      };
      if (form.course) payload.course = form.course;
      if (trimmed.password) {
        payload.password = trimmed.password;
        payload.confirm_password = trimmed.confirmPassword;
      }

      await apiClient.put(`/api/auth/teachers/${id}/`, payload);
      setSuccess('Teacher updated successfully.');
      setTimeout(() => navigate('/teachers'), 600);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          'Unable to update teacher. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageTemplate title="Edit Teacher" subtitle="Update teacher details">
        <p style={{ textAlign: 'center', padding: '40px' }}>Loading teacher details…</p>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate title="Edit Teacher" subtitle="Update teacher account details">
      <div className="add-teacher-panel">
        <form className="add-teacher-grid" onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <input
              id="name"
              name="name"
              type="text"
              value={form.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="name@example.com"
              value={form.email}
              onChange={handleChange}
              required
            />
          </div>
          <PhoneInput
            id="phone"
            label="Mobile Number"
            value={form.phone}
            onChange={(phone) => setForm((prev) => ({ ...prev, phone }))}
            required
            disabled={submitting}
          />
          <div className="form-group">
            <label htmlFor="course">Course (optional)</label>
            <select
              id="course"
              name="course"
              value={form.course}
              onChange={handleChange}
              className="select-input"
              disabled={loadingCourses}
            >
              <option value="">Choose a course</option>
              {courses.map((course) => (
                <option key={course.course_id || course.id} value={course.course_id || course.id}>
                  {course.course_name || course.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="password">New Password (optional)</label>
            <input
              id="password"
              name="password"
              type="password"
              placeholder="Leave blank to keep current"
              value={form.password}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={form.confirmPassword}
              onChange={handleChange}
            />
          </div>

          {error && <p className="form-error form-group-wide">{error}</p>}
          {success && <p className="form-success form-group-wide">{success}</p>}

          <div className="add-teacher-actions">
            <button
              type="button"
              className="btn-primary btn-secondary"
              onClick={() => navigate('/teachers')}
            >
              Cancel
            </button>
            <button type="submit" className="primary-btn" disabled={submitting}>
              {submitting ? 'Updating…' : 'Update Teacher'}
            </button>
          </div>
        </form>
      </div>
    </PageTemplate>
  );
}

export default EditTeacher;
